import React from "react";
import {
  Music,
  Mic,
  Target,
  ArrowRight,
  BookOpen,
  Lightbulb,
  Trophy,
  PartyPopper,
  X,
} from "lucide-react";

interface SecondaryButton {
  text: string;
  onClick: () => void;
}

interface PracticeIntroModalProps {
  isOpen: boolean;
  onStart: () => void;
  onClose?: () => void;
  title?: string;
  missionText?: string;
  steps?: string[];
  variant?: "practice" | "learn" | "completed";
  buttonText?: string;
  secondaryButton?: SecondaryButton;
  footerTip?: string;
  dismissOnBackdrop?: boolean;
}

export const PracticeIntroModal: React.FC<PracticeIntroModalProps> = ({
  isOpen,
  onStart,
  onClose,
  title = "Modo Práctica",
  missionText = "Toca cada nota que se ilumina en el mástil. El sistema te escucha en tiempo real.",
  steps = [
    "Observa la nota resaltada en el mástil",
    "Toca la misma nota en tu guitarra",
    "Recibe feedback inmediato: verde si acertás, rojo si no",
    "Avanzá automáticamente a la siguiente nota",
  ],
  variant = "practice",
  buttonText,
  secondaryButton,
  footerTip,
  dismissOnBackdrop = true,
}) => {
  if (!isOpen) return null;

  const isLearn = variant === "learn";
  const isCompleted = variant === "completed";
  const Icon = isCompleted ? PartyPopper : isLearn ? BookOpen : Music;
  const accent = isCompleted ? "#D4A373" : isLearn ? "#5BA8A8" : "var(--bordona-accent)";
  const accentHover = isCompleted ? "#B8823C" : isLearn ? "#4A9898" : "var(--bordona-accent-hover)";
  const defaultButtonText = isCompleted
    ? "Siguiente misión"
    : isLearn
    ? "Comenzar a aprender"
    : "Empezar práctica";
  const subtitle = isCompleted
    ? "¡Lo lograste!"
    : isLearn
    ? "Exploración guiada"
    : "Modo con micrófono";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={dismissOnBackdrop ? onClose : undefined}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal
        className="relative w-full max-w-lg rounded-2xl border border-white/12 bg-[#141414]/95 shadow-2xl backdrop-blur-xl overflow-hidden"
      >
        {/* Cabecera */}
        <div className="relative px-5 pt-5 pb-4 border-b border-white/10">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at top left, ${accent}44, transparent 60%)`,
            }}
          />
          <div className="relative flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border border-white/15"
              style={{ backgroundColor: `${accent}22`, color: accent }}
            >
              {isCompleted ? <Trophy className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0 pr-8">
              <p
                className="text-[10px] font-sans font-semibold uppercase tracking-wider mb-0.5"
                style={{ color: accent }}
              >
                {subtitle}
              </p>
              <h2 className="text-lg font-serif font-bold text-slate-100 leading-snug">{title}</h2>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="absolute top-0 right-0 w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-200 hover:bg-white/10 transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 space-y-4 max-h-[min(60vh,28rem)] overflow-y-auto">
          <div className="flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.06] border border-white/10">
            <Target className="w-5 h-5 shrink-0 mt-0.5" style={{ color: accent }} />
            <p className="text-sm font-sans text-slate-300 leading-relaxed">{missionText}</p>
          </div>

          <div className="space-y-2.5">
            <p className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-500 px-0.5">
              {isLearn || isCompleted ? "Qué vas a hacer" : "Pasos"}
            </p>
            <ol className="space-y-2">
              {steps.map((step, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.04] border border-white/[0.06]"
                >
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-sans font-bold shrink-0 border border-white/10"
                    style={{ color: accent, backgroundColor: `${accent}18` }}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-sm font-sans text-slate-400 leading-relaxed pt-0.5">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div
            className="flex items-start gap-2.5 p-3 rounded-xl border border-white/10"
            style={{ backgroundColor: `${accent}0d` }}
          >
            <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent }} />
            <p className="text-[11px] font-sans text-slate-400 leading-relaxed">
              <span className="font-semibold text-slate-300">
                {isLearn ? "Recuerda:" : isCompleted ? "Siguiente:" : "Consejo:"}
              </span>{" "}
              {footerTip ??
                (isLearn
                  ? "Explorá con calma; el pentagrama y el teclado muestran solo las notas del paso."
                  : isCompleted
                  ? "Cada paso construye tu mapa mental del instrumento."
                  : "Tocá cerca del micrófono con volumen medio.")}
            </p>
          </div>
        </div>

        {/* Pie */}
        <div className="px-5 py-4 border-t border-white/10 bg-black/20 flex flex-col gap-2">
          <button
            type="button"
            onClick={onStart}
            className="w-full h-11 rounded-xl text-white text-sm font-sans font-semibold flex items-center justify-center gap-2 shadow-lg transition-all hover:brightness-110"
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = accentHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = accent;
            }}
          >
            <span>{buttonText ?? defaultButtonText}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          {secondaryButton ? (
            <button
              type="button"
              onClick={secondaryButton.onClick}
              className="w-full h-9 rounded-xl text-xs font-sans font-medium text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-all"
            >
              {secondaryButton.text}
            </button>
          ) : (
            onClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-full h-9 rounded-xl text-xs font-sans font-medium text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-all"
              >
                Cancelar
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};
